import React, { useContext } from 'react';

import { TableHead, TableCell, TableSortLabel, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DashboardContext } from '../../../context/DashboardContext';
import { type DashboardStudentTableColumns, type Order } from '../types';

const HeadCell = styled(TableCell)`
    font-weight: bold;
    text-align: center;
`;

const HeadCellLabel = styled(TableSortLabel)`
    text-align: center;
`;

interface DashboardTableHeadProps {
    order: Order;
    orderBy: string;
    onRequestSort: (event: React.MouseEvent<unknown>, property: DashboardStudentTableColumns) => void;
}

interface HeadCellData {
    id: DashboardStudentTableColumns;
    label: string;
}

const TableHeader = styled(TableHead)`
    width: 100%;
`;

const DashboardTableHead: React.FC<DashboardTableHeadProps> = ({ order, orderBy, onRequestSort }) => {
    const { classSettings } = useContext(DashboardContext);
    const { t } = useTranslation('dashboard');
    const { t: tGeneric } = useTranslation('generic');
    const { schoolSettings } = useContext(DashboardContext);

    const headCells: HeadCellData[] = schoolSettings.has_lose_focus_tracking
        ? [
            { id: 'sortName', label: tGeneric('Name') },
            { id: 'role', label: tGeneric('Role') },
            { id: 'talkTime', label: t('talk-time') },
            { id: 'focusTime', label: t('focus-time') },
            { id: 'handRaisedCount', label: t('hand-raises') },
            { id: 'feedbackCount', label: t('feedback') },
            { id: 'starsMax', label: t('stars') }
        ]
        : [
            { id: 'sortName', label: t('student') },
            { id: 'role', label: tGeneric('Role') },
            { id: 'talkTime', label: t('talk-time') },
            { id: 'handRaisedCount', label: t('hand-raises') },
            { id: 'feedbackCount', label: t('feedback') },
            { id: 'starsMax', label: t('stars') }
        ];

    const createSortHandler = (property: DashboardStudentTableColumns) => (event: React.MouseEvent) => {
        onRequestSort(event, property);
    };

    return (
        <TableHeader>
            <TableRow>
                {headCells.map((headCell, index) => {
                    const hideCell = (headCell.id === 'focusTime') && !classSettings?.has_lose_focus_tracking;
                    if (hideCell) {
                        return null;
                    }

                    return (
                        <HeadCell key={index} align={'center'} sortDirection={orderBy === headCell.id ? order : false}>
                            <HeadCellLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                                key={`label_${index}`}
                            >
                                {headCell.label}
                            </HeadCellLabel>
                        </HeadCell>
                    );
                })}
            </TableRow>
        </TableHeader>
    );
};

export default DashboardTableHead;
